import { useEffect, useRef, useState } from "react";
import type { FlyerMediaAttachment } from "./flyerMediaUpload";
import "./TemplateChatAttachments.css";

export interface FlyerMediaAssetPick {
    s3_key?: string | null;
    url: string;
    type: "image" | "video";
    caption?: string;
}

export type FlyerMediaAttachProps = {
    attachments: FlyerMediaAttachment[];
    onAttachmentsChange: (next: FlyerMediaAttachment[]) => void;
    myAssets: FlyerMediaAssetPick[];
    disabled?: boolean;
    inline?: boolean;
};

function makeId() {
    return `flyer-att-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function closeMenus(setMenuOpen: (v: boolean) => void, setAssetsOpen: (v: boolean) => void) {
    setMenuOpen(false);
    setAssetsOpen(false);
}

export default function FlyerMediaAttach({
    attachments,
    onAttachmentsChange,
    myAssets,
    disabled,
    inline = false,
}: FlyerMediaAttachProps) {
    const rootRef = useRef<HTMLDivElement>(null);
    const fileRef = useRef<HTMLInputElement>(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [assetsOpen, setAssetsOpen] = useState(false);

    useEffect(() => {
        if (!menuOpen && !assetsOpen) return;
        const onPointerDown = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
                closeMenus(setMenuOpen, setAssetsOpen);
            }
        };
        document.addEventListener("mousedown", onPointerDown);
        return () => document.removeEventListener("mousedown", onPointerDown);
    }, [menuOpen, assetsOpen]);

    const addFiles = (files: FileList | File[]) => {
        const list = Array.from(files);
        const next = [...attachments];
        for (const file of list) {
            if (!file.type.startsWith("image/")) continue;
            next.push({
                id: makeId(),
                file,
                previewUrl: URL.createObjectURL(file),
                filename: file.name,
            });
        }
        onAttachmentsChange(next.slice(0, 8));
        closeMenus(setMenuOpen, setAssetsOpen);
    };

    const addFromAsset = (asset: FlyerMediaAssetPick) => {
        if (!asset.url || !asset.s3_key) return;
        onAttachmentsChange(
            [
                ...attachments,
                {
                    id: makeId(),
                    previewUrl: asset.url,
                    url: asset.url,
                    s3_key: asset.s3_key,
                    filename: asset.caption?.slice(0, 40) || "My asset",
                    fromAsset: true,
                },
            ].slice(0, 8),
        );
        closeMenus(setMenuOpen, setAssetsOpen);
    };

    const remove = (id: string) => {
        const item = attachments.find((a) => a.id === id);
        if (item?.previewUrl && item.file) URL.revokeObjectURL(item.previewUrl);
        onAttachmentsChange(attachments.filter((a) => a.id !== id));
    };

    const plusControl = (
        <div className="tmpl-chat-attach-actions">
            <button
                type="button"
                className="tmpl-chat-attach-plus"
                disabled={disabled}
                title="Attach image"
                aria-label="Attach image"
                aria-expanded={menuOpen}
                onClick={() => {
                    setMenuOpen((o) => !o);
                    setAssetsOpen(false);
                }}
            >
                +
            </button>
            {menuOpen && (
                <div className="tmpl-chat-attach-menu">
                    <button
                        type="button"
                        onClick={() => {
                            closeMenus(setMenuOpen, setAssetsOpen);
                            fileRef.current?.click();
                        }}
                    >
                        Upload from computer
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            setMenuOpen(false);
                            setAssetsOpen(true);
                        }}
                    >
                        Upload from My Assets
                    </button>
                </div>
            )}
        </div>
    );

    const assetsPicker = assetsOpen && (
        <div
            className={
                inline
                    ? "tmpl-chat-attach-assets tmpl-chat-attach-assets--popover"
                    : "tmpl-chat-attach-assets"
            }
        >
            {myAssets.filter((a) => a.type === "image" && a.s3_key).length === 0 ? (
                <p className="tmpl-chat-attach-assets-empty">No image assets yet.</p>
            ) : (
                myAssets
                    .filter((a) => a.type === "image" && a.s3_key)
                    .slice(0, 16)
                    .map((a) => (
                        <button
                            key={a.s3_key!}
                            type="button"
                            className="tmpl-chat-attach-asset-pick"
                            onClick={() => addFromAsset(a)}
                        >
                            <img src={a.url} alt="" />
                        </button>
                    ))
            )}
        </div>
    );

    if (inline) {
        return (
            <div ref={rootRef} className="tmpl-chat-attach tmpl-chat-attach--inline">
                {plusControl}
                <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    onChange={(e) => {
                        if (e.target.files?.length) addFiles(e.target.files);
                        e.target.value = "";
                    }}
                />
                {assetsPicker}
            </div>
        );
    }

    return (
        <div ref={rootRef} className="tmpl-chat-attach">
            {attachments.length > 0 && (
                <div className="tmpl-chat-attach-previews">
                    {attachments.map((a) => (
                        <div key={a.id} className="tmpl-chat-attach-thumb">
                            <img src={a.previewUrl} alt="" />
                            <button
                                type="button"
                                className="tmpl-chat-attach-remove"
                                onClick={() => remove(a.id)}
                                aria-label="Remove"
                            >
                                ×
                            </button>
                        </div>
                    ))}
                </div>
            )}
            <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => {
                    if (e.target.files?.length) addFiles(e.target.files);
                    e.target.value = "";
                }}
            />
            {assetsPicker}
        </div>
    );
}
